import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { ReactNode, useRef } from 'react'

import stopPropagationHandler from '@src/quince-site-common/src/utils/stopPropogationHandler'

import useLockBodyScroll from './hooks/useLockBodyScroll'
import Cross from './icons/cross.inline.svg'
import styles from './modal.module.scss'

export interface ModalProps {
  children: ReactNode
  open: boolean
  onClose?: () => void
  className?: string
  closeButtonClassName?: string
  modalWrapperClassName?: string
  closeOnBGClick?: boolean
  showCloseButton?: boolean
  crossAlignment?: 'left' | 'right'
  closeButtonContainerClassName?: string
  variant?: 'default' | 'bottomSheet'
  responsiveBottomSheet?: boolean
}

const Modal = (props: ModalProps) => {
  const { t } = useTranslation('common')
  const {
    closeOnBGClick = true,
    open,
    crossAlignment = 'right',
    variant = 'default',
    responsiveBottomSheet = false,
  } = props
  const contentRef = useRef<HTMLDivElement>(null)

  useLockBodyScroll(open, contentRef)

  if (!open) return null

  const isBottomSheet = variant === 'bottomSheet'

  return (
    <div
      onClick={closeOnBGClick ? props.onClose : undefined}
      className={classNames(
        styles.module_container,
        isBottomSheet && styles.module_container_bottomSheet,
        responsiveBottomSheet && styles.module_container_responsiveBottomSheet,
        props.modalWrapperClassName,
      )}
    >
      <div
        ref={contentRef}
        onClick={stopPropagationHandler}
        onTouchMove={stopPropagationHandler}
        className={classNames(
          styles.modal,
          isBottomSheet && styles.modal_bottomSheet,
          responsiveBottomSheet && styles.modal_responsiveBottomSheet,
          props.className,
        )}
      >
        {props.showCloseButton && (
          <div className={props.closeButtonContainerClassName}>
            <Cross
              aria-label={t('close_button')}
              data-testid="modal-close-button"
              onClick={props.onClose}
              className={classNames(styles.cross_icon, props.closeButtonClassName, {
                [styles.cross_icon_left ?? '']: crossAlignment === 'left',
                [styles.cross_icon_right ?? '']: crossAlignment === 'right',
              })}
            />
          </div>
        )}
        {props.children}
      </div>
    </div>
  )
}

export default Modal
